import React, { useState } from 'react';
import { ActivityIcon, CheckIcon, RefreshCwIcon, TicketIcon } from 'lucide-react';
import { fetchBandsintownShows, getBandsintownStatus, mergeBandsintownShows, saveBandsintownStatus } from '../../data/bandsintown';
import type { WebsiteShow } from '../../data/website';
import { Button } from '../ui/Button';
interface BandsintownPanelProps {
  artistName: string;
  appId: string;
  autoSync: boolean;
  shows: WebsiteShow[];
  onFieldChange: (key: 'bandsintownArtist' | 'bandsintownAppId', value: string) => void;
  onAutoSyncChange: (autoSync: boolean) => void;
  onImport: (shows: WebsiteShow[]) => void;
}
export function BandsintownPanel({
  artistName,
  appId,
  autoSync,
  shows,
  onFieldChange,
  onAutoSyncChange,
  onImport
}: BandsintownPanelProps) {
  const [status, setStatus] = useState(() => getBandsintownStatus());
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [testing, setTesting] = useState(false);
  const [report, setReport] = useState<{
    ok: boolean;
    headline: string;
    lines: string[];
  } | null>(null);
  const connected = Boolean(artistName.trim() && appId.trim());

  /**
   * Asks Bandsintown the same question the public site asks on every visit, and
   * shows exactly what came back — so a missing date can be traced to either
   * Bandsintown or the settings here, rather than guessed at.
   */
  const test = async () => {
    setTesting(true);
    setReport(null);
    const result = await fetchBandsintownShows(artistName, appId);
    setTesting(false);
    if (result.error) {
      setReport({
        ok: false,
        headline: result.error,
        lines: []
      });
      return;
    }
    if (!result.shows.length) {
      setReport({
        ok: false,
        headline: 'Bandsintown answered, but returned no upcoming dates for this artist.',
        lines: ['Check the artist name above matches your Bandsintown profile exactly.', 'Dates in the past are never returned, and unpublished dates are not visible.']
      });
      return;
    }
    setReport({
      ok: true,
      headline: `Bandsintown returned ${result.shows.length} upcoming date${result.shows.length === 1 ? '' : 's'}.`,
      lines: result.shows.slice(0, 6).map(show => `${show.date} · ${show.venue} · ${show.city}`)
    });
  };
  const sync = async () => {
    setSyncing(true);
    setError('');
    const result = await fetchBandsintownShows(artistName, appId);
    setSyncing(false);
    if (result.error) {
      setError(result.error);
      return;
    }
    onImport(mergeBandsintownShows(shows, result.shows));
    const nextStatus = {
      lastSyncedAt: new Date().toISOString(),
      importedCount: result.shows.length
    };
    saveBandsintownStatus(nextStatus);
    setStatus(nextStatus);
  };
  return <div className="mb-3 rounded-xl border border-accent/20 bg-accent/[0.06] p-3" data-id="element-887" data-mp="3fzb3lmpikyh2">
      <div className="flex flex-wrap items-center justify-between gap-2" data-id="element-888" data-mp="3kzywyf17treg">
        <span className="flex items-center gap-1.5 text-[12px] font-medium text-zinc-200" data-id="element-889" data-mp="3dfwkbusyllc0">
          <TicketIcon className="h-3.5 w-3.5 text-accent-soft" data-id="element-890" data-mp="3dhr5na857fgm" /> Bandsintown
        </span>
        <span className="text-[11px] text-zinc-500" data-id="element-891" data-mp="38j67dgcgb5ut">
          {!connected ? 'Not connected' : autoSync ? <span className="inline-flex items-center gap-1 text-grow" data-id="element-892" data-mp="3hgzymcvcz9b4">
              <CheckIcon className="h-3 w-3" data-id="element-893" data-mp="2trxb4oe1mm8p" /> Auto-syncing
            </span> : status ? `${status.importedCount} dates · synced ${formatSyncTime(status.lastSyncedAt)}` : 'Ready to sync'}
        </span>
      </div>

      <div className="mt-3 grid gap-2 sm:grid-cols-2" data-id="element-894" data-mp="33j1sfur6yj6p">
        <SmallField label="Bandsintown artist name" value={artistName} placeholder="Cooper Ty Band" onChange={value => onFieldChange('bandsintownArtist', value)} data-id="element-895" data-mp="2yqs0yaafhb2b" />
        <SmallField label="App ID" value={appId} placeholder="Your Bandsintown app ID" onChange={value => onFieldChange('bandsintownAppId', value)} data-id="element-896" data-mp="261l5jk69t1la" />
      </div>

      <label className="mt-3 flex cursor-pointer items-start gap-2.5 rounded-lg border border-white/[0.08] bg-ink-850 p-2.5" data-id="element-897" data-mp="2ooobxrheeuu1">
        <input type="checkbox" checked={autoSync} disabled={!connected} onChange={event => onAutoSyncChange(event.target.checked)} className="mt-0.5 h-4 w-4 shrink-0 accent-[#d97742] disabled:opacity-40" data-id="element-898" data-mp="2tlet6i4xvwh1" />
        <span data-id="element-899" data-mp="37dcyehvj587x">
          <span className="block text-[12px] font-medium text-zinc-200" data-id="element-900" data-mp="37cte4uieq7qq">Auto-sync on the public site</span>
          <span className="mt-0.5 block text-[10.5px] leading-relaxed text-zinc-500" data-id="element-901" data-mp="3cbo36e6ywsja">
            Dates load straight from Bandsintown every time someone visits, so a show added there appears here without
            syncing or publishing. Your published dates stay as the fallback if Bandsintown is unreachable.
          </span>
        </span>
      </label>

      <p className="mt-2 text-[10.5px] leading-relaxed text-zinc-500" data-id="element-902" data-mp="44q5za3tgq3o">
        Request a free app ID from Bandsintown for Artists. Manual syncing replaces imported dates and keeps any shows
        you added by hand.
      </p>

      {error && <p role="alert" className="mt-2 text-[11px] text-rose-300" data-id="element-903" data-mp="3reeelja33c98">
          {error}
        </p>}

      <div className="mt-3 flex flex-wrap gap-2" data-id="element-904" data-mp="3mfuulof1waji">
        <Button variant="secondary" size="sm" onClick={sync} disabled={!connected || syncing} data-id="element-905" data-mp="n1999irxvjsb">
          <RefreshCwIcon className={syncing ? 'h-3.5 w-3.5 animate-spin' : 'h-3.5 w-3.5'} data-id="element-906" data-mp="j20cs65il7t2" />
          {syncing ? 'Syncing…' : 'Sync now'}
        </Button>
        <Button variant="secondary" size="sm" onClick={() => void test()} disabled={!connected || testing} data-id="element-907" data-mp="92rqlzgw4xh9">
          <ActivityIcon className="h-3.5 w-3.5" data-id="element-908" data-mp="2yrtpyqkzte9d" /> {testing ? 'Checking…' : 'Test the connection'}
        </Button>
      </div>

      {report && <div className="mt-3 rounded-lg border border-white/[0.08] bg-ink-850 p-2.5" data-id="element-909" data-mp="2trff70s0bq7n">
          <p className={`text-[11.5px] font-medium ${report.ok ? 'text-emerald-300' : 'text-rose-300'}`} data-id="element-910" data-mp="2vb8mox9tg8dl">
            {report.headline}
          </p>
          {report.lines.length > 0 && <ul className="mt-1.5 space-y-0.5" data-id="element-911" data-mp="2m8fxczk726qe">
              {report.lines.map(line => <li key={line} className="text-[11px] text-zinc-400" data-id="element-912" data-mp="2r7srm5deag30">
                  {line}
                </li>)}
            </ul>}
        </div>}
    </div>;
}
function SmallField({
  label,
  value,
  onChange,
  placeholder
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  return <label className="block" data-id="element-913" data-mp="27kbkwdbqirg2">
      <span className="mb-1 block text-[11px] font-medium text-zinc-400" data-id="element-914" data-mp="27ledqqsmb3ym">{label}</span>
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="w-full rounded-lg border border-white/[0.1] bg-ink-850 px-2.5 py-2 text-[12.5px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70" data-id="element-915" data-mp="1tkk5q99hf50h" />
    </label>;
}
function formatSyncTime(timestamp: string): string {
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return 'recently';
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}